// src/components/home/MarketNarrative.js
import React from "react";
import { motion } from "framer-motion";
import marketImg from "../assets/market-evolution.jpg";
import aiImg from "../assets/ai-shift.jpg";
import bridgeImg from "../assets/tradfi-defi-bridge.jpg";

const MarketNarrative = () => {
  const sections = [
    {
      id: 1,
      title: "The Market is Evolving",
      text: `Institutional adoption of on-chain assets is accelerating — but infrastructure remains fragmented.
QNC delivers the missing layer between regulated capital and decentralized markets, where strategies, fees, and reporting are executed transparently and verifiably on-chain.`,
      img: marketImg,
      reverse: false,
    },
    {
      id: 2,
      title: "The AI & Automation Era",
      text: `Modern fund management is moving beyond discretionary execution.
QNC’s automation layer integrates AI-powered analytics and smart-contract precision — creating self-adjusting strategies that trade, report, and distribute profits autonomously.`,
      img: aiImg,
      reverse: true,
    },
    {
      id: 3,
      title: "Connecting Traditional and Decentralized Markets",
      text: `QNC serves as the connective layer between traditional allocators and decentralized markets.
Built on Enzyme’s audited smart contracts and Kraken’s custodial framework, QNC enables on-chain execution with off-chain confidence — merging compliance, transparency, and real-time performance reporting.`,
      img: bridgeImg,
      reverse: false,
    },
  ];

  return (
    <section className="relative bg-gradient-to-b from-[#050E24] via-[#0A2A66] to-[#050E24] text-white py-32 overflow-hidden">
      {/* Animated background glows */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/4 w-[700px] h-[700px] bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-2xl animate-pulse delay-700"></div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        {sections.map((sec, index) => (
          <motion.div
            key={sec.id}
            className={`max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-16 px-6 mb-28 ${
              sec.reverse ? "md:flex-row-reverse" : ""
            }`}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: "easeOut", delay: index * 0.15 }}
          >
            {/* Image container with reveal mask */}
            <motion.div
              className="relative w-full md:w-1/2 overflow-hidden rounded-2xl shadow-2xl"
              initial={{ clipPath: "inset(0 100% 0 0)" }}
              whileInView={{ clipPath: "inset(0 0% 0 0)" }}
              transition={{ duration: 1, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/25 to-transparent z-10 rounded-2xl"></div>

              {/* Floating AI nodes only on section 2 */}
              {sec.id === 2 && (
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  animate={{ y: [0, -15, 0], opacity: [0.3, 0.6, 0.3] }}
                  transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
                >
                  <div className="absolute left-1/4 top-1/3 w-8 h-8 bg-cyan-400/10 rounded-full blur-lg" />
                  <div className="absolute right-1/3 bottom-1/4 w-10 h-10 bg-blue-400/10 rounded-full blur-lg" />
                  <div className="absolute left-1/2 bottom-1/3 w-5 h-5 bg-cyan-300/10 rounded-full blur-md" />
                </motion.div>
              )}

              <img
                src={sec.img}
                alt={sec.title}
                className="w-full h-[400px] object-cover rounded-2xl opacity-95 hover:opacity-100 transition duration-500"
              />
            </motion.div>

            {/* Text container */}
            <motion.div
              className="w-full md:w-1/2"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 + index * 0.15 }}
            >
              <h3 className="text-3xl md:text-4xl font-bold mb-6 leading-snug tracking-tight">
                <span className="text-white">
                  {sec.title.split(" ")[0]}{" "}
                </span>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent animate-gradient-flow">
                  {sec.title.split(" ").slice(1).join(" ")}
                </span>
              </h3>
              <p className="text-lg text-gray-200 leading-relaxed max-w-xl">
                {sec.text}
              </p>
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Gradient divider bottom */}
      <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-transparent"></div>
    </section>
  );
};

export default MarketNarrative;
